import React, { useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import ahoy from 'ahoy.js';
import client from '../lib/apolloClient';
import AppLayout from '../components/Succeedo/Layout/AppLayout';
import DealStructureWizard from '../components/Succeedo/DealStructure/DealStructureWizard';
import DealStructureResults from '../components/Succeedo/DealStructure/DealStructureResults';
import ShareSnapshotModal from '../components/Succeedo/DealTeam/ShareSnapshotModal';

const GET_DEAL_STRUCTURE_DATA = gql`
  query GetDealStructureData {
    currentUser {
      id
      succeedo_country
      succeedo_region
      deal_teams {
        id
        name
        status
      }
    }
    introduction_requests
    featured_partners(limit: 6)
    deal_structure_snapshots(limit: 5)
    valuation_snapshots(limit: 5)
  }
`;

const CALCULATE_DEAL_STRUCTURE = gql`
  mutation CalculateDealStructure(
    $askingPrice: Float!
    $structure: String
    $downPayment: Float
    $sellerNoteAmount: Float
    $repaymentTermYears: Int
    $interestRate: Float
    $earnoutAmount: Float
    $earnoutDurationYears: Int
    $province: String
    $expectedCloseMonths: Int
    $consultingAnnualFee: Float
    $consultingDurationYears: Int
    $annualRevenue: Float
    $annualExpenses: Float
    $revenueGrowthRate: Float
    $expenseGrowthRate: Float
    $cashFlowLength: Int
  ) {
    calculate_deal_structure(
      asking_price: $askingPrice
      structure: $structure
      down_payment: $downPayment
      seller_note_amount: $sellerNoteAmount
      repayment_term_years: $repaymentTermYears
      interest_rate: $interestRate
      earnout_amount: $earnoutAmount
      earnout_duration_years: $earnoutDurationYears
      province: $province
      expected_close_months: $expectedCloseMonths
      consulting_annual_fee: $consultingAnnualFee
      consulting_duration_years: $consultingDurationYears
      annual_revenue: $annualRevenue
      annual_expenses: $annualExpenses
      revenue_growth_rate: $revenueGrowthRate
      expense_growth_rate: $expenseGrowthRate
      cash_flow_length: $cashFlowLength
    ) {
      result
      errors
    }
  }
`;

const SAVE_DEAL_STRUCTURE_SNAPSHOT = gql`
  mutation SaveDealStructureSnapshot($inputs: JSON!, $results: JSON!, $structureType: String!) {
    save_deal_structure_snapshot(inputs: $inputs, results: $results, structure_type: $structureType) {
      snapshot
      errors
    }
  }
`;

const SET_PRIMARY_DEAL_STRUCTURE_SNAPSHOT = gql`
  mutation SetPrimaryDealStructureSnapshot($id: ID!) {
    set_primary_deal_structure_snapshot(id: $id) {
      snapshot
      errors
    }
  }
`;

const CREATE_INTRO_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!, $coachProfileId: ID) {
    create_introduction_request(partner_category: $partnerCategory, coach_profile_id: $coachProfileId) {
      introduction_request
      errors
    }
  }
`;

function DealStructureInner() {
  const { data, loading, error, refetch } = useQuery(GET_DEAL_STRUCTURE_DATA);
  const [calculateDealStructure, { loading: calcLoading }] = useMutation(CALCULATE_DEAL_STRUCTURE);
  const [saveDealStructureSnapshot] = useMutation(SAVE_DEAL_STRUCTURE_SNAPSHOT);
  const [setPrimaryDealStructureSnapshot] = useMutation(SET_PRIMARY_DEAL_STRUCTURE_SNAPSHOT);
  const [createIntroRequest, { loading: introLoading }] = useMutation(CREATE_INTRO_REQUEST);

  const [dealResult, setDealResult] = useState(null);
  const [dealInputs, setDealInputs] = useState(null);
  const [currentSnapshotId, setCurrentSnapshotId] = useState(null);
  const [wizardInitialValues, setWizardInitialValues] = useState(null);
  const [valuationContext, setValuationContext] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const introductionRequests = data?.introduction_requests || [];
  const allPartners = data?.featured_partners || [];
  const accountantPartners = allPartners.filter(
    (p) => (p.partner_category || '').toLowerCase() === 'accountant'
  );
  const lawyerPartners = allPartners.filter(
    (p) => (p.partner_category || '').toLowerCase() === 'lawyer'
  );
  const dealStructureSnapshots = data?.deal_structure_snapshots || [];

  // Active deal team (first one) — drives Share with team affordance.
  const activeDealTeam = data?.currentUser?.deal_teams?.[0] || null;

  // Build preselected snapshot payload from the currently saved snapshot, if any.
  const currentSnapshot = dealStructureSnapshots.find((s) => s.id === currentSnapshotId);
  const preselectedSnapshot = currentSnapshot
    ? {
        id: currentSnapshot.id,
        type: 'deal_structure_snapshot',
        label: `Deal Structure — ${new Date(currentSnapshot.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}${currentSnapshot.is_primary ? ' (primary)' : ''}`,
        is_primary: !!currentSnapshot.is_primary,
      }
    : null;

  // All valuation snapshots for wizard Step 0
  const valuationSnapshots = data?.valuation_snapshots || [];

  // Compute default asking price from latest valuation snapshot
  const latestValuation = valuationSnapshots[0] || null;
  const defaultAskingPrice = latestValuation?.value_range
    ? Math.round(
        ((parseFloat(latestValuation.value_range.low) || 0) +
          (parseFloat(latestValuation.value_range.high) || 0)) /
          2
      )
    : null;

  const handleCalculate = async (params, selectedSnapshot = null) => {
    // Build valuation context from selected snapshot if provided
    let newValuationContext = null;
    if (selectedSnapshot) {
      const inputs = selectedSnapshot.inputs || {};
      newValuationContext = {
        revenue: parseFloat(inputs.revenue) || 0,
        operatingExpenses: parseFloat(inputs.operating_expenses) || 0,
        growthRate: parseFloat(inputs.growth_rate) || 0,
        province: inputs.province || null,
        sectorCluster: selectedSnapshot.sector_cluster || inputs.sector_cluster || null,
        snapshotDate: selectedSnapshot.created_at || null,
      };
      setValuationContext(newValuationContext);
    }

    try {
      const { data: result } = await calculateDealStructure({
        variables: {
          askingPrice: params.askingPrice,
          structure: params.structure,
          downPayment: params.downPayment || null,
          sellerNoteAmount: params.sellerNoteAmount || null,
          repaymentTermYears: params.repaymentTermYears || null,
          interestRate: params.interestRate || null,
          earnoutAmount: params.earnoutAmount || null,
          earnoutDurationYears: params.earnoutDurationYears || null,
          province: params.province || 'ON',
          expectedCloseMonths: params.expectedCloseMonths || 6,
          consultingAnnualFee: params.consultingAnnualFee || null,
          consultingDurationYears: params.consultingDurationYears || null,
          annualRevenue: params.annualRevenue || null,
          annualExpenses: params.annualExpenses || null,
          revenueGrowthRate: params.revenueGrowthRate || null,
          expenseGrowthRate: params.expenseGrowthRate || null,
          cashFlowLength: params.cashFlowLength || null,
        },
      });

      const errors = result?.calculate_deal_structure?.errors || [];
      if (errors.length > 0) {
        console.error('Calculate deal structure errors:', errors);
        return;
      }

      const calculatedResult = result?.calculate_deal_structure?.result;
      if (calculatedResult) {
        setDealResult(calculatedResult);
        setDealInputs(params);
      }
    } catch (e) {
      console.error('Failed to calculate deal structure', e);
    }
  };

  // Recalculate handler for live updates from results page
  // The results page no longer sends a structure type — the backend auto-detects from amounts.
  const handleRecalculate = async (params) => {
    try {
      const { data: result } = await calculateDealStructure({
        variables: {
          askingPrice: params.askingPrice,
          structure: params.structure || 'auto',
          downPayment: params.downPayment || null,
          sellerNoteAmount: params.sellerNoteAmount || null,
          repaymentTermYears: params.repaymentTermYears || null,
          interestRate: params.interestRate || null,
          earnoutAmount: params.earnoutAmount || null,
          earnoutDurationYears: params.earnoutDurationYears || null,
          province: params.province || 'ON',
          expectedCloseMonths: params.expectedCloseMonths || 6,
          consultingAnnualFee: params.consultingAnnualFee || null,
          consultingDurationYears: params.consultingDurationYears || null,
          annualRevenue: params.annualRevenue || null,
          annualExpenses: params.annualExpenses || null,
          revenueGrowthRate: params.revenueGrowthRate || null,
          expenseGrowthRate: params.expenseGrowthRate || null,
          cashFlowLength: params.cashFlowLength || null,
        },
      });

      const errors = result?.calculate_deal_structure?.errors || [];
      if (errors.length > 0) {
        console.error('Recalculate deal structure errors:', errors);
        return;
      }

      const calculatedResult = result?.calculate_deal_structure?.result;
      if (calculatedResult) {
        setDealResult(calculatedResult);
        setDealInputs(params);
      }
    } catch (e) {
      console.error('Failed to recalculate deal structure', e);
    }
  };

  const handleSaveSnapshot = async ({ inputs, results }) => {
    try {
      const { data: result } = await saveDealStructureSnapshot({
        variables: {
          inputs: inputs || dealInputs,
          results: results || dealResult,
          structureType: (results || dealResult)?.structure_type || (inputs || dealInputs)?.structure || 'cash',
        },
      });

      const errors = result?.save_deal_structure_snapshot?.errors || [];
      if (errors.length > 0) {
        console.error('Save deal structure snapshot errors:', errors);
        return null;
      }

      const snapshot = result?.save_deal_structure_snapshot?.snapshot;
      if (snapshot) {
        setCurrentSnapshotId(snapshot.id);
      }

      refetch();
      ahoy.track('succeedo_deal_structure_saved', { snapshot_id: snapshot?.id });
      return snapshot;
    } catch (e) {
      console.error('Failed to save deal structure snapshot', e);
      return null;
    }
  };

  const handleLoadSnapshot = (snapshot) => {
    setCurrentSnapshotId(snapshot.id);

    // Restore result from snapshot
    if (snapshot.results) {
      setDealResult(snapshot.results);
    }

    // Restore wizard inputs from snapshot
    if (snapshot.inputs) {
      const inp = snapshot.inputs;
      setDealInputs(inp);
      setWizardInitialValues({
        askingPrice: String(inp.askingPrice || inp.asking_price || '750000'),
        expectedCloseMonths: inp.expectedCloseMonths || inp.expected_close_months || 6,
        structure: inp.structure || 'cash',
        province: inp.province || 'ON',
        downPayment: String(inp.downPayment || inp.down_payment || ''),
        repaymentTermYears: inp.repaymentTermYears || inp.repayment_term_years || 5,
        interestRate: String(inp.interestRate || inp.interest_rate || '5.0'),
        earnoutAmount: String(inp.earnoutAmount || inp.earnout_amount || ''),
        earnoutDurationYears: inp.earnoutDurationYears || inp.earnout_duration_years || 2,
        consultingAnnualFee: String(inp.consultingAnnualFee || inp.consulting_annual_fee || ''),
        consultingDurationYears: inp.consultingDurationYears || inp.consulting_duration_years || 1,
      });
    }
  };

  const handleSetPrimary = async (snapshotId) => {
    try {
      await setPrimaryDealStructureSnapshot({
        variables: { id: String(snapshotId) },
      });
      refetch();
    } catch (e) {
      console.error('Failed to set primary deal structure snapshot', e);
    }
  };

  const handleRequestIntroduction = async (category) => {
    try {
      const { data: result } = await createIntroRequest({
        variables: { partnerCategory: category },
      });
      const errors = result?.create_introduction_request?.errors || [];
      if (errors.length > 0) {
        console.error('Intro request errors:', errors);
        return;
      }
      ahoy.track('succeedo_introduction_requested', {
        partner_category: category,
        source: 'deal_structure',
      });
      refetch();
    } catch (e) {
      console.error('Failed to create intro request', e);
    }
  };

  const handleBack = () => {
    setDealResult(null);
  };

  return (
    <AppLayout breadcrumb="DEAL STRUCTURE">
      {loading && (
        <div className="max-w-4xl mx-auto py-8 px-4">
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      )}
      {error && (
        <div className="max-w-4xl mx-auto py-8 px-4">
          <p className="text-red-500 text-sm">
            Error loading deal structure data: {error.message}
          </p>
        </div>
      )}
      {!loading && !error && !dealResult && (
        <DealStructureWizard
          country={data?.currentUser?.succeedo_country}
          region={data?.currentUser?.succeedo_region}
          onCalculate={handleCalculate}
          loading={calcLoading}
          initialValues={wizardInitialValues}
          defaultAskingPrice={defaultAskingPrice}
          valuationSnapshots={valuationSnapshots}
          dealStructureSnapshots={dealStructureSnapshots}
          onLoadDealStructureSnapshot={handleLoadSnapshot}
        />
      )}
      {!loading && !error && dealResult && (
        <DealStructureResults
          result={dealResult}
          inputs={dealInputs}
          valuationContext={valuationContext}
          onBack={handleBack}
          onRecalculate={handleRecalculate}
          recalculating={calcLoading}
          onSaveSnapshot={handleSaveSnapshot}
          onLoadSnapshot={handleLoadSnapshot}
          onSetPrimary={handleSetPrimary}
          onRequestIntroduction={handleRequestIntroduction}
          introLoading={introLoading}
          introductionRequests={introductionRequests}
          dealStructureSnapshots={dealStructureSnapshots}
          currentSnapshotId={currentSnapshotId}
          accountantPartners={accountantPartners}
          lawyerPartners={lawyerPartners}
          dealTeam={activeDealTeam}
          onShareWithTeam={preselectedSnapshot ? () => setShareModalOpen(true) : undefined}
        />
      )}
      {activeDealTeam && preselectedSnapshot && (
        <ShareSnapshotModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onSuccess={() => {
            if (typeof refetch === 'function') refetch();
          }}
          dealTeamId={activeDealTeam.id}
          dealTeamName={activeDealTeam.name}
          preselectedSnapshot={preselectedSnapshot}
        />
      )}
    </AppLayout>
  );
}

const SucceedoDealStructureContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <DealStructureInner />
  </ApolloProvider>
);

export default SucceedoDealStructureContainer;
